import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { workerNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import SignatureCanvas from "react-signature-canvas";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  FileSignature,
  CheckCircle,
  Shield,
  AlertTriangle,
  Loader2,
  FileText,
  User,
  Eraser,
  Percent
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorkerSignContract() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [tlContributionPercent, setTlContributionPercent] = useState(0);
  const [hazardChecks, setHazardChecks] = useState({});
  const [ppeChecks, setPpeChecks] = useState({});
  const signaturePadRef = useRef(null);

  // PPE emoji mapping
  const ppeEmojis = {
    helmet: "⛑️",
    safety_shoes: "👢",
    hi_vis: "🦺",
    harness: "🪢",
    gloves: "🧤",
    ear_protection: "🎧",
    safety_glasses: "🥽",
    dust_mask: "😷",
    face_shield: "🛡️",
    knee_pads: "🦵",
    respirator: "😮‍💨",
    safety_harness: "🪢"
  };

  // Hazard label mapping
  const hazardLabels = {
    fall_height: "Fall fra høyde",
    falling_objects: "Fallende gjenstander",
    electrical: "Elektrisk fare",
    chemical: "Kjemisk fare",
    noise: "Støy",
    dust: "Støv",
    sharp_edges: "Skarpe kanter",
    heavy_lifting: "Tunge løft",
    confined_space: "Trang plass",
    weather: "Værforhold"
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const isSignatureEmpty = () => {
    return signaturePadRef.current?.isEmpty() ?? true;
  };

  // Check if all SJA items are checked
  const allSjaChecked = () => {
    const sja = data?.project?.sja_form;
    if (!sja) return true;
    
    const hazards = sja.hazards || [];
    const customHazards = sja.custom_hazards || [];
    const ppeRequired = sja.ppe_required || [];
    
    const allHazardsChecked = hazards.every((_, idx) => hazardChecks[`hazard_${idx}`]);
    const allCustomHazardsChecked = customHazards.every((_, idx) => hazardChecks[`custom_${idx}`]);
    const allPpeChecked = ppeRequired.every((ppe) => ppeChecks[ppe]);
    
    return allHazardsChecked && allCustomHazardsChecked && allPpeChecked;
  };

  useEffect(() => {
    fetchContractData();
  }, [projectId, token]);

  const fetchContractData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects-v3/team-contract/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      if (user?.name) {
        setSignerName(user.name);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
      toast.error("Kunne ikke laste kontrakt");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!allSjaChecked()) {
      toast.error(t('contracts.checkAllSja') || 'Vennligst sjekk alle farer og verneutstyr før signering');
      return;
    }
    if (!agreedToTerms) {
      toast.error(t('contracts.mustAgreeToTerms') || 'Du må godta vilkårene');
      return;
    }
    if (!signerName.trim()) {
      toast.error(t('contracts.enterYourName') || 'Vennligst skriv inn ditt navn');
      return;
    }
    if (isSignatureEmpty()) {
      toast.error(t('contracts.pleaseSign') || 'Vennligst tegn signaturen din');
      return;
    }

    const signatureData = signaturePadRef.current?.toDataURL('image/png');

    setSigning(true);
    try {
      await axios.post(
        `${API_URL}/api/projects-v3/worker/sign-contract/${projectId}`,
        { 
          signer_name: signerName,
          signature_url: signatureData,
          sja_confirmed: true,
          tl_contribution_percent: tlContributionPercent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('contracts.contractSigned') || 'Kontrakt signert!');
      // Redirect to view contract page
      navigate(`/worker/contract/${projectId}`);
    } catch (error) {
      console.error("Error signing:", error);
      toast.error(error.response?.data?.detail || "Kunne ikke signere kontrakt");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={workerNavItems} title={t('contracts.signContract')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  // Check if TL has signed
  if (!data?.project?.tl_signed_at) {
    return (
      <DashboardLayout navItems={workerNavItems} title={t('contracts.contract')}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('contracts.waitingTLSignature') || 'Venter på Team Leader'}</h2>
          <p className="text-gray-400 mb-6">Team Leader må signere først før du kan signere.</p>
          <Button onClick={() => navigate("/worker/my-projects")} className="bg-[#23F7DD] text-black">
            {t('nav.myProjects') || 'Mine Prosjekter'}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Check if already signed
  const hasAlreadySigned = data?.project?.worker_signatures?.some(
    sig => sig.user_id === user?.user_id
  );
  
  if (hasAlreadySigned) {
    return (
      <DashboardLayout navItems={workerNavItems} title={t('contracts.contract')}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('contracts.alreadySigned') || 'Kontrakt allerede signert'}</h2>
          <p className="text-gray-400 mb-6">Du har allerede signert denne kontrakten.</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate(`/worker/contract/${projectId}`)} className="bg-[#23F7DD] text-black">
              {t('contracts.viewContract') || 'Se Kontrakt'}
            </Button>
            <Button onClick={() => navigate("/worker/my-projects")} variant="outline" className="border-gray-500 text-gray-300">
              {t('nav.myProjects') || 'Mine Prosjekter'}
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const { project, application, fee_breakdown } = data || {};
  
  const calculateDuration = () => {
    if (project?.duration_days) return `${project.duration_days} dager`;
    if (project?.start_date && project?.end_date) {
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return `${days} dager`;
    }
    return "TBD";
  };

  // Calculate actual team size from application (TL + workers)
  const teamMembersCount = application?.team_members?.length || 0;
  const actualTeamSize = 1 + teamMembersCount; // 1 TL + workers
  
  const netToTeam = project?.net_to_team || (fee_breakdown?.net_to_team) || 0;
  const baseShare = netToTeam / actualTeamSize;

  return (
    <DashboardLayout navItems={workerNavItems} title={t('contracts.signContract')}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
              {t('contracts.signTeamContract') || 'Signer Teamkontrakt'}
            </h1>
            <p className="text-gray-400 mt-1">
              {t('contracts.reviewAndSign') || 'Gjennomgå og signer kontrakten for'} {project?.title}
            </p>
          </div>
        </div>

        {/* TL Signed Status */}
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-white font-medium">Team Leader har signert</p>
                <p className="text-green-400 text-sm">
                  Signert {project?.tl_signed_at ? new Date(project.tl_signed_at).toLocaleDateString('nb-NO') : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#23F7DD]" />
              {t('projectDetail.projectDetails') || 'Prosjektdetaljer'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-500 text-sm">{t('projects.project') || 'Prosjekt'}</p>
                <p className="text-white font-semibold">{project?.title}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.location') || 'Lokasjon'}</p>
                <p className="text-white flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  {project?.location_city}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.startDate') || 'Startdato'}</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-green-400" />
                  {project?.start_date ? new Date(project.start_date).toLocaleDateString('nb-NO') : "TBD"}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.endDate') || 'Sluttdato'}</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-red-400" />
                  {project?.end_date ? new Date(project.end_date).toLocaleDateString('nb-NO') : "TBD"}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-gray-500 text-sm">{t('projects.duration') || 'Varighet'}</p>
                <p className="text-white font-semibold">{calculateDuration()}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">{t('projects.projectType') || 'Prosjekttype'}</p>
                <p className="text-white capitalize">{project?.project_type?.replace("_", " ") || "Stillastjenester"}</p>
              </div>
            </div>
            
            {project?.description && (
              <div className="mt-4 p-4 bg-white/5 rounded-lg">
                <p className="text-gray-500 text-sm mb-2">{t('projects.description') || 'Beskrivelse'}</p>
                <p className="text-gray-300">{project.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SJA - Safety Analysis */}
        {project?.sja_form && (
          <Card className="bg-[#0A0A0A] border-orange-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-400" />
                Sikker Jobb Analyse (SJA)
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Viktig</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Job Description */}
              {project.sja_form.job_description && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-500 text-sm mb-1">Jobbeskrivelse</p>
                  <p className="text-white">{project.sja_form.job_description}</p>
                </div>
              )}
              
              {/* Emergency Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {project.sja_form.emergency_contact && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">📞 Nødkontakt</p>
                    <p className="text-white font-bold text-lg">{project.sja_form.emergency_contact}</p>
                  </div>
                )}
                
                {project.sja_form.nearest_hospital && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">🏥 Nærmeste sykehus</p>
                    <p className="text-white font-semibold">{project.sja_form.nearest_hospital}</p>
                  </div>
                )}
                
                {project.sja_form.first_aid_location && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 text-sm mb-1">🩹 Førstehjelpsutstyr</p>
                    <p className="text-white font-semibold">{project.sja_form.first_aid_location}</p>
                  </div>
                )}
              </div>
              
              {/* Hazards with Checkboxes */}
              {project.sja_form.hazards && project.sja_form.hazards.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    ⚠️ Identifiserte farer - Kryss av for å bekrefte at du forstår
                  </p>
                  <div className="space-y-3">
                    {project.sja_form.hazards.map((hazard, idx) => {
                      const hazardId = typeof hazard === 'string' ? hazard : (hazard.id || `hazard_${idx}`);
                      const hazardLabel = hazardLabels[hazardId] || hazardId.replace(/_/g, ' ');
                      const hazardMeasures = typeof hazard === 'object' ? hazard.measures : null;
                      const hazardResponsible = typeof hazard === 'object' ? hazard.responsible : null;
                      const isChecked = hazardChecks[`hazard_${idx}`];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border transition-all ${
                            isChecked 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-red-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`hazard_${idx}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => setHazardChecks(prev => ({...prev, [`hazard_${idx}`]: checked}))}
                              className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <div className="flex-1">
                              <label htmlFor={`hazard_${idx}`} className="text-white font-semibold cursor-pointer capitalize flex items-center gap-2">
                                ⚠️ {hazardLabel}
                                {isChecked && <CheckCircle className="w-4 h-4 text-green-400" />}
                              </label>
                              
                              {hazardMeasures && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                                  <p className="text-sm text-green-400 font-medium">✅ Forebyggende tiltak:</p>
                                  <p className="text-gray-300 text-sm">{hazardMeasures}</p>
                                </div>
                              )}
                              
                              {hazardResponsible && (
                                <p className="text-sm text-purple-400 mt-1">
                                  👤 Ansvarlig: {hazardResponsible}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Custom Hazards */}
              {project.sja_form.custom_hazards && project.sja_form.custom_hazards.length > 0 && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    🔶 Tilleggsfarer
                  </p>
                  <div className="space-y-3">
                    {project.sja_form.custom_hazards.map((hazard, idx) => {
                      const isChecked = hazardChecks[`custom_${idx}`];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border transition-all ${
                            isChecked 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-orange-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`custom_${idx}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => setHazardChecks(prev => ({...prev, [`custom_${idx}`]: checked}))}
                              className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <div className="flex-1">
                              <label htmlFor={`custom_${idx}`} className="text-white font-semibold cursor-pointer flex items-center gap-2">
                                🔶 {hazard.description || `Tilleggsfare ${idx + 1}`}
                                {isChecked && <CheckCircle className="w-4 h-4 text-green-400" />}
                              </label>
                              
                              {hazard.measures && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                                  <p className="text-sm text-green-400 font-medium">✅ Forebyggende tiltak:</p>
                                  <p className="text-gray-300 text-sm">{hazard.measures}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* PPE Required */}
              {project.sja_form.ppe_required && project.sja_form.ppe_required.length > 0 && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    🛡️ Påkrevd verneutstyr - Kryss av for å bekrefte at du har det
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {project.sja_form.ppe_required.map((ppe, idx) => {
                      const isChecked = ppeChecks[ppe];
                      const emoji = ppeEmojis[ppe] || "🔧";
                      const ppeLabel = ppe.replace(/_/g, ' ');
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border transition-all cursor-pointer ${
                            isChecked 
                              ? 'bg-green-500/20 border-green-500/50' 
                              : 'bg-white/5 border-blue-500/30 hover:border-blue-500/50'
                          }`}
                          onClick={() => setPpeChecks(prev => ({...prev, [ppe]: !prev[ppe]}))}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => setPpeChecks(prev => ({...prev, [ppe]: checked}))}
                              className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <span className="text-2xl">{emoji}</span>
                            <span className={`text-sm capitalize ${isChecked ? 'text-green-400' : 'text-white'}`}>
                              {ppeLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Additional Safety Measures */}
              {project.sja_form.additional_measures && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm mb-1">📋 Tilleggstiltak</p>
                  <p className="text-gray-300">{project.sja_form.additional_measures}</p>
                </div>
              )}
              
              {/* SJA Completion Status */}
              <div className={`p-3 rounded-lg border ${
                allSjaChecked() 
                  ? 'bg-green-500/20 border-green-500/50' 
                  : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  {allSjaChecked() ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">✅ Alle sikkerhetspunkter bekreftet</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-semibold">⚠️ Vennligst kryss av alle farer og verneutstyr ovenfor før signering</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#23F7DD]" />
              Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Team Leader */}
              <div className="flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{application?.team_leader_name || project?.team_leader_name}</p>
                    <p className="text-purple-400 text-sm">Team Leader</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 text-sm">Signert</span>
                </div>
              </div>
              
              {/* Workers */}
              {application?.team_members?.map((member, idx) => {
                const isCurrentUser = member.user_id === user?.user_id;
                const hasSigned = project?.worker_signatures?.some(s => s.user_id === member.user_id);
                
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCurrentUser 
                        ? 'bg-[#23F7DD]/10 border-[#23F7DD]/30' 
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold">{member.name}</p>
                          {member.worker_code && (
                            <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                              {member.worker_code}
                            </Badge>
                          )}
                          {isCurrentUser && (
                            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] text-xs">
                              {t('common.you') || 'Deg'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-blue-400 text-sm">{member.role || 'Arbeider'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasSigned ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 text-sm">Signert</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-5 h-5 text-yellow-400" />
                          <span className="text-yellow-400 text-sm">Venter</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Financial Details - Only show net_to_team and personal share (team_budget is for Holding only) */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.financialTerms') || 'Økonomiske vilkår'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.netToTeam') || 'Netto til team'}</p>
                  <p className="text-sm text-gray-500 mb-1">({actualTeamSize} {t('common.members') || 'medlemmer'}, {t('common.beforeTax') || 'før skatt'})</p>
                  <p className="text-2xl font-bold text-green-400">
                    {Math.round(netToTeam).toLocaleString()} kr
                  </p>
                </div>
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.yourBaseShare') || 'Din grunnandel'}</p>
                  <p className="text-sm text-gray-500 mb-1">({t('common.beforeTax') || 'før skatt'})</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {Math.round(baseShare).toLocaleString()} kr
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* TL Contribution Slider */}
        <Card className="bg-[#0A0A0A] border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Percent className="w-5 h-5 text-purple-400" />
              {t('contracts.tlContribution') || 'Bidrag til Team Leader'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-purple-300 text-sm">
                {t('contracts.tlContributionInfo') || 'Du kan velge å gi Team Leader en prosentandel (0-3%) av din andel som kompensasjon for ledelse og koordinering av teamet.'}
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-gray-400">{t('contracts.contributionPercent') || 'Bidragsprosent'}</Label>
                <span className="text-3xl font-bold text-purple-400">{tlContributionPercent}%</span>
              </div>
              
              <Slider
                value={[tlContributionPercent]}
                onValueChange={(value) => setTlContributionPercent(value[0])}
                max={3}
                min={0}
                step={0.5}
                className="w-full"
              />
              
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>1%</span>
                <span>2%</span>
                <span>3%</span>
              </div>
              
              {/* Preview calculation */}
              <div className="mt-4 p-4 bg-[#0d0d1a] rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Din grunnandel:</span>
                  <span className="text-white font-semibold">{Math.round(baseShare).toLocaleString()} kr</span>
                </div>
                {tlContributionPercent > 0 && (
                  <div className="flex items-center justify-between text-purple-400">
                    <span>Bidrag til TL ({tlContributionPercent}%):</span>
                    <span>-{Math.round(baseShare * tlContributionPercent / 100).toLocaleString()} kr</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <span className="text-gray-300 font-medium">Din endelige andel:</span>
                  <span className="text-[#23F7DD] font-bold text-xl">
                    {Math.round(baseShare * (1 - tlContributionPercent / 100)).toLocaleString()} kr
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.contractTerms') || 'Kontraktsvilkår'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <p>Ved å signere denne kontrakten godtar du:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Å fullføre stillasarbeidet som spesifisert i prosjektkravene</li>
                <li>Å følge alle sikkerhetsforskrifter og AllStillas retningslinjer</li>
                <li>Å gi daglige fremdriftsoppdateringer under prosjektet</li>
                <li>Å fullføre arbeidet innen den angitte tidsrammen</li>
                <li>Å akseptere de økonomiske vilkårene som beskrevet ovenfor</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.signContract') || 'Signer Kontrakt'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="signerName" className="text-white">
                {t('contracts.fullNameForSignature') || 'Fullt navn for signatur'}
              </Label>
              <Input
                id="signerName"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder={t('contracts.enterFullName') || 'Skriv inn ditt fulle navn'}
                className="bg-white/5 border-white/20 text-white"
              />
            </div>

            {/* Signature Pad */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white">{t('contracts.yourSignature') || 'Din signatur'}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearSignature}
                  className="border-white/20 text-gray-400 hover:text-white hover:bg-white/10"
                >
                  <Eraser className="w-4 h-4 mr-1" />
                  {t('common.clear') || 'Tøm'}
                </Button>
              </div>
              <div className="border-2 border-dashed border-white/30 rounded-lg bg-white overflow-hidden">
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{
                    className: "w-full h-32",
                    style: { width: '100%', height: '150px' }
                  }}
                  penColor="black"
                  backgroundColor="white"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                {t('contracts.drawSignature') || 'Tegn signaturen din ovenfor med mus eller berøring'}
              </p>
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                className="mt-1 data-[state=checked]:bg-[#23F7DD] data-[state=checked]:border-[#23F7DD]"
              />
              <label htmlFor="terms" className="text-gray-300 cursor-pointer">
                {t('contracts.agreeToTerms') || 'Jeg har lest og godtar kontraktsvilkårene og sikkerhetsanalysen (SJA)'}
              </label>
            </div>

            {/* Sign Button */}
            <Button
              onClick={handleSign}
              disabled={signing || !agreedToTerms || !signerName.trim() || !allSjaChecked()}
              className="w-full bg-[#23F7DD] hover:bg-[#1de9cf] text-black font-bold py-6 text-lg disabled:opacity-50"
            >
              {signing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('contracts.signingContract') || 'Signerer...'}
                </>
              ) : (
                <>
                  <FileSignature className="w-5 h-5 mr-2" />
                  {t('contracts.signContract') || 'Signer Kontrakt'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
